import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, MousePointer, TrendingUp } from 'lucide-react';
import { BaseCalculatorMeta } from '../components/BaseCalculatorMeta';
import { CALCULATOR_PATHS } from '../constants/calculatorPaths';

const CPCCalculator = () => {
  const [adCost, setAdCost] = useState(1000);
  const [clicks, setClicks] = useState(500);
  const [cpc, setCpc] = useState(0);

  useEffect(() => {
    calculateCPC();
  }, [adCost, clicks]);

  const calculateCPC = () => {
    if (clicks === 0) {
      setCpc(0);
      return;
    }
    const calculatedCpc = adCost / clicks;
    setCpc(parseFloat(calculatedCpc.toFixed(2)));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <BaseCalculatorMeta 
        path={CALCULATOR_PATHS.CPC}
        title="CPC Calculator: Free Cost Per Click Calculator Tool"
        description="Calculate your Cost Per Click (CPC) instantly with our free calculator. Optimize your PPC campaigns and advertising spend effectively."
        keywords="CPC calculator, cost per click calculator, PPC calculator, advertising metrics, digital marketing tools"
        schemaFeatures={[
          "Calculate Cost Per Click",
          "Ad cost tracking",
          "Click tracking",
          "CPC optimization tips"
        ]}
      />
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        CPC (Cost Per Click) Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Ad Cost</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={adCost}
                  onChange={(e) => setAdCost(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Number of Clicks</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MousePointer className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={clicks}
                  onChange={(e) => setClicks(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${cpc}
            <TrendingUp className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Cost Per Click (CPC)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation</h2>
        <p className="text-gray-600">
          Your Cost Per Click (CPC) is ${cpc}. This means you're spending ${cpc} for each click on your advertisements.
          {cpc > 2 ? " This CPC is relatively high - consider optimizing your targeting and ad content." : 
           cpc > 1 ? " This CPC is moderate - there may be room for optimization." : 
           " This CPC is relatively low - your campaigns are cost-efficient."}
        </p>
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Improve CPC</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Improve your Quality Score by enhancing ad relevance</li>
          <li>Optimize your keyword targeting</li>
          <li>Test different ad copies and formats</li>
          <li>Adjust your bidding strategy</li>
          <li>Target more specific audiences</li>
        </ul>
      </div>
    </div>
  );
};

export default CPCCalculator;
